import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { AppNotificationAlert, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '@/layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath } from '@/routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import EnvironmentalAspectsAssessmentAspectTitleCard from '@/layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/Aspect/EnvironmentalAspectsAssessmentAspectTitleCard';
import EnvironmentalAspectsAssessmentSidebar from '@/layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/Sidebar/EnvironmentalAspectsAssessmentSidebar';
import EnvironmentalAspectsAssessmentAspectContainer from '@/layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/EnvironmentalAspectsAssessmentAspectContainer';
import AppSpinner from '@/layout/components/AppSpinner';
import EnvironmentalAspectsAssessmentTopButtons from '@/layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/EnvironmentalAspectsAssessmentTopButtons';
import EnvironmentalAspectsAssessmentBase from './EnvironmentalAspectsAssessmentBase';

export default function EnvironmentalAspectsAssessmentAssessmentCurrentScreen() {
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const assessment = useSelector((s) => s?.currentPage?.assessment);
  const aspects = useSelector((s) => s?.currentPage?.aspects);
  const checkedCards = useSelector((s) => s?.currentPage?.checkedCards);
  const hiddenNoApplyAspects = useSelector(
    (state) => state.currentPage?.hiddenNoApplyAspects,
  );
  const showOnlyUnAssessed = useSelector(
    (state) => state.currentPage?.showOnlyUnAssessed
  );
  const showOnlySignificant = useSelector(
    (state) => state.currentPage?.showOnlySignificant,
  );
  const [loading, setLoading] = useState(true);
  const [breadcrumb, setBreadcrumb] = useState();

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD ASSESSMENT
  const loadAssessment = async () =>
    axios
      .post(
        axiosPath('api_r_user_module_environmentalAspectsAssessment_assessment_get'),
        {
          moduleBridgeHex,
          plantHex,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change('assessment', data?.assessment));
          dispatch(currentPageAction.change('checkedCards', data?.checkedCards));
          dispatch(currentPageAction.change('aspects', data?.aspects));
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    loadAssessment();
  }, []);
  // < LOAD ASSESSMENT

  // > SAVE ASSESSMENT
  const handleSave = async () =>
    axios
      .post(
        axiosPath('api_r_user_module_environmentalAspectsAssessment_assessment_edit'),
        {
          moduleBridgeHex,
          plantHex,
          assessmentID: assessment?.id,
          checkedCards,
          aspects,
        },
      )
      .then(() => {
        if (isMounted.current) {
          AppNotificationAlert({
            title: 'Completado',
            message: 'Evaluación guardada con éxito',
            type: 'success',
            duration: 3000,
          });
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  // < SAVE ASSESSMENT

  // > TOGGLE CLOSE ASSESSMENT
  const handleToggleClose = async () =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_environmentalAspectsAssessment_assessment_toggleClose',
        ),
        {
          moduleBridgeHex,
          plantHex,
          assessmentID: assessment?.id,
          checkedCards,
          aspects,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          dispatch(
            currentPageAction.change('assessment', {
              ...assessment,
              closed: data?.closed,
            }),
          );

          AppNotificationAlert({
            title: 'Completado',
            message: data?.closed
              ? 'Evaluación cerrada con éxito'
              : 'Evaluación reabierta con éxito',
            type: 'success',
            duration: 3000,
          });
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  // < TOGGLE CLOSE ASSESSMENT

  useEffect(() => {
    if (moduleData) {
      const b = [{ name: moduleData?.moduleName }];

      if (moduleData?.plant) {
        b.unshift({
          name: moduleData?.plant.name,
          route: {
            name: 'user_module_legalCompliance_evaluation',
            params: {
              moduleBridgeHex: moduleData?.plant?.moduleBridgeHex,
              plantHex,
              page: 1,
              filtersHex: 0,
            },
          },
        });
      }

      setBreadcrumb(b);
    } else {
      setBreadcrumb([{ name: '...' }]);
    }
  }, [moduleData, assessment]);

  const showCard = useCallback(
    (aspectID) => {
      return (
        (!hiddenNoApplyAspects || checkedCards.includes(aspectID)) &&
        (!showOnlyUnAssessed || aspects[aspectID]?.evaluation !== 1) &&
        (!showOnlySignificant || aspects[aspectID]?.evaluation === 1)
      );
    },
    [aspects, hiddenNoApplyAspects, showOnlyUnAssessed, showOnlySignificant],
  );

  return (
    <>
      <EnvironmentalAspectsAssessmentBase />
      <EnvironmentalAspectsAssessmentSidebar
        activeRoute="user_module_environmentalAspectsAssessment_assessment_current"
        assessmentTitle={moduleData?.assessmentTitle}
      />
      <BreadcrumbHandler paths={breadcrumb} />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <EnvironmentalAspectsAssessmentTopButtons
            activeRoute="user_module_environmentalAspectsAssessment_assessment_current"
            ah={assessment?.hex}
          />
          {assessment ? (
            <>
              <h4 className="my-4">{moduleData?.assessmentTitle}</h4>
              {assessment?.groups?.map((g, i) => (
                <React.Fragment key={i}>
                  <EnvironmentalAspectsAssessmentAspectTitleCard color="warning">
                    {g?.name}
                  </EnvironmentalAspectsAssessmentAspectTitleCard>
                  {g?.subGroups?.map((sg, j) => (
                    <React.Fragment key={j}>
                      <EnvironmentalAspectsAssessmentAspectTitleCard
                        className="ml-4"
                        color="info"
                      >
                        {sg?.name}
                      </EnvironmentalAspectsAssessmentAspectTitleCard>
                      {sg?.aspects?.map((aspectID, k) => (
                        <React.Fragment key={`${k}-${aspectID}`}>
                          {showCard(aspectID) && (
                            <EnvironmentalAspectsAssessmentAspectContainer
                              id={aspectID}
                              type="sacyrAspect"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              {!assessment?.closed && (
                <SaveBtn className="btn btn-success btn-sm" onClick={handleSave}>
                  Guardar
                </SaveBtn>
              )}

              <ToggleCloseBtn
                className={`btn btn${
                  assessment?.closed ? '-outline' : ''
                }-danger btn-sm`}
                onClick={handleToggleClose}
              >
                {assessment?.closed ? 'Reabrir' : 'Cerrar'}
              </ToggleCloseBtn>
            </>
          ) : loading ? (
            <AppSpinner>
              <AppRemixIcon icon="loader-4" size={40} />
            </AppSpinner>
          ) : (
            <div className="alert alert-info">
              No se ha encontrado ninguna evaluación
            </div>
          )}
        </div>
      </main>
    </>
  );
}

const SaveBtn = styled.button`
  position: fixed;
  bottom: 20px;
  right: 90px;
  z-index: 100;
`;

const ToggleCloseBtn = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
`;
